import _ from "lodash";

function hasOwn(object, key) {
    return Object.prototype.hasOwnProperty.call(object, key);
}

function getSchemaPathSegments(schema, fieldName) {
    let schemaNode = schema;
    let remainingFieldName = fieldName;
    let pathSegments = [];

    while (remainingFieldName) {
        let schemaProperties = _.get(schemaNode, "properties");
        if (!_.isPlainObject(schemaProperties)) {
            return fieldName.split("/");
        }

        if (hasOwn(schemaProperties, remainingFieldName)) {
            pathSegments.push(remainingFieldName);
            return pathSegments;
        }

        let separatorIndex = remainingFieldName.indexOf("/");
        let propertyName =
            separatorIndex === -1
                ? remainingFieldName
                : remainingFieldName.slice(0, separatorIndex);
        if (!hasOwn(schemaProperties, propertyName)) {
            return fieldName.split("/");
        }

        pathSegments.push(propertyName);
        schemaNode = schemaProperties[propertyName];
        remainingFieldName =
            separatorIndex === -1
                ? ""
                : remainingFieldName.slice(separatorIndex + 1);
    }

    return pathSegments;
}

function getDataForSchemaField(data, schema, fieldName) {
    if (!_.isPlainObject(data)) {
        return { found: false };
    }

    if (hasOwn(data, fieldName)) {
        return { found: true, value: data[fieldName] };
    }

    let currentValue = data;
    let pathSegments = getSchemaPathSegments(schema, fieldName);
    for (let pathSegment of pathSegments) {
        if (
            currentValue === null ||
            typeof currentValue !== "object" ||
            !hasOwn(currentValue, pathSegment)
        ) {
            return { found: false };
        }
        currentValue = currentValue[pathSegment];
    }

    return { found: true, value: currentValue };
}

function setDataForSchemaField(data, schema, fieldName, value) {
    let pathSegments = getSchemaPathSegments(schema, fieldName);
    if (pathSegments.length === 1) {
        data[pathSegments[0]] = value;
        return;
    }

    let currentData = data;
    _.forEach(pathSegments, function (pathSegment, index) {
        if (index === pathSegments.length - 1) {
            currentData[pathSegment] = value;
            return;
        }

        if (
            currentData[pathSegment] === null ||
            typeof currentData[pathSegment] !== "object" ||
            _.isArray(currentData[pathSegment])
        ) {
            currentData[pathSegment] = {};
        }
        currentData = currentData[pathSegment];
    });
}

function findSchemaPropertyAtPath(schemaNode, pathSegments, pathIndex) {
    if (!_.isPlainObject(schemaNode)) {
        return;
    }

    let propertyName = pathSegments[pathIndex];
    let schemaProperties = _.get(schemaNode, "properties", {});
    if (hasOwn(schemaProperties, propertyName)) {
        let schemaProperty = schemaProperties[propertyName];
        if (pathIndex === pathSegments.length - 1) {
            return schemaProperty;
        }

        let nestedSchemaProperty = findSchemaPropertyAtPath(
            schemaProperty,
            pathSegments,
            pathIndex + 1,
        );
        if (nestedSchemaProperty) {
            return nestedSchemaProperty;
        }
    }

    let foundSchemaProperty;
    _.forEach(["allOf", "anyOf", "oneOf"], function (schemaKeyword) {
        _.forEach(schemaNode[schemaKeyword], function (childSchema) {
            if (foundSchemaProperty) {
                return;
            }
            foundSchemaProperty = findSchemaPropertyAtPath(
                childSchema,
                pathSegments,
                pathIndex,
            );
        });
    });

    _.forEach(["if", "then", "else"], function (schemaKeyword) {
        if (foundSchemaProperty) {
            return;
        }
        foundSchemaProperty = findSchemaPropertyAtPath(
            schemaNode[schemaKeyword],
            pathSegments,
            pathIndex,
        );
    });

    _.forEach(schemaNode.dependencies, function (dependencySchema) {
        if (foundSchemaProperty || _.isArray(dependencySchema)) {
            return;
        }
        foundSchemaProperty = findSchemaPropertyAtPath(
            dependencySchema,
            pathSegments,
            pathIndex,
        );
    });

    _.forEach(schemaNode.dependentSchemas, function (dependentSchema) {
        if (foundSchemaProperty) {
            return;
        }
        foundSchemaProperty = findSchemaPropertyAtPath(
            dependentSchema,
            pathSegments,
            pathIndex,
        );
    });

    return foundSchemaProperty;
}

function getSchemaPropertyAtPath(schema, fieldName) {
    return findSchemaPropertyAtPath(
        schema,
        getSchemaPathSegments(schema, fieldName),
        0,
    );
}

function collectSchemaPropertiesAtPath(
    schemaNode,
    pathSegments,
    pathIndex,
    schemaPropertiesAtPath,
) {
    if (!_.isPlainObject(schemaNode)) {
        return;
    }

    let propertyName = pathSegments[pathIndex];
    let schemaProperties = _.get(schemaNode, "properties", {});
    if (hasOwn(schemaProperties, propertyName)) {
        let schemaProperty = schemaProperties[propertyName];
        if (pathIndex === pathSegments.length - 1) {
            schemaPropertiesAtPath.push(schemaProperty);
        } else {
            collectSchemaPropertiesAtPath(
                schemaProperty,
                pathSegments,
                pathIndex + 1,
                schemaPropertiesAtPath,
            );
        }
    }

    _.forEach(["allOf", "anyOf", "oneOf"], function (schemaKeyword) {
        _.forEach(schemaNode[schemaKeyword], function (childSchema) {
            collectSchemaPropertiesAtPath(
                childSchema,
                pathSegments,
                pathIndex,
                schemaPropertiesAtPath,
            );
        });
    });

    _.forEach(["if", "then", "else"], function (schemaKeyword) {
        collectSchemaPropertiesAtPath(
            schemaNode[schemaKeyword],
            pathSegments,
            pathIndex,
            schemaPropertiesAtPath,
        );
    });

    _.forEach(schemaNode.dependencies, function (dependencySchema) {
        if (!_.isArray(dependencySchema)) {
            collectSchemaPropertiesAtPath(
                dependencySchema,
                pathSegments,
                pathIndex,
                schemaPropertiesAtPath,
            );
        }
    });

    _.forEach(schemaNode.dependentSchemas, function (dependentSchema) {
        collectSchemaPropertiesAtPath(
            dependentSchema,
            pathSegments,
            pathIndex,
            schemaPropertiesAtPath,
        );
    });
}

function getSchemaPropertiesAtPath(schema, fieldName) {
    let schemaPropertiesAtPath = [];
    collectSchemaPropertiesAtPath(
        schema,
        getSchemaPathSegments(schema, fieldName),
        0,
        schemaPropertiesAtPath,
    );
    return schemaPropertiesAtPath;
}

function isSchemaObjectContainer(schemaNode) {
    return (
        _.isPlainObject(schemaNode) &&
        _.isPlainObject(schemaNode.properties) &&
        schemaNode.type === "object" &&
        !schemaNode["x-input-type"] &&
        !schemaNode.oneOf &&
        !schemaNode.anyOf
    );
}

function collectSchemaPropertyKeys(
    schema,
    prefix,
    fieldNames,
    objectFieldNames,
) {
    if (!_.isPlainObject(schema)) {
        return;
    }

    let schemaProperties = _.get(schema, "properties");
    _.forEach(schemaProperties, function (propertySchema, propertyName) {
        let fieldName = prefix ? prefix + "/" + propertyName : propertyName;
        if (isSchemaObjectContainer(propertySchema)) {
            objectFieldNames.push(fieldName);
            collectSchemaPropertyKeys(
                propertySchema,
                fieldName,
                fieldNames,
                objectFieldNames,
            );
            return;
        }
        fieldNames.push(fieldName);
    });

    _.forEach(["allOf", "anyOf", "oneOf"], function (schemaKeyword) {
        _.forEach(schema[schemaKeyword], function (childSchema) {
            collectSchemaPropertyKeys(
                childSchema,
                prefix,
                fieldNames,
                objectFieldNames,
            );
        });
    });

    _.forEach(["if", "then", "else"], function (schemaKeyword) {
        collectSchemaPropertyKeys(
            schema[schemaKeyword],
            prefix,
            fieldNames,
            objectFieldNames,
        );
    });

    _.forEach(schema.dependencies, function (dependencySchema) {
        if (!_.isArray(dependencySchema)) {
            collectSchemaPropertyKeys(
                dependencySchema,
                prefix,
                fieldNames,
                objectFieldNames,
            );
        }
    });

    _.forEach(schema.dependentSchemas, function (dependentSchema) {
        collectSchemaPropertyKeys(
            dependentSchema,
            prefix,
            fieldNames,
            objectFieldNames,
        );
    });
}

export function getSchemaPropertyKeys(schema) {
    let fieldNames = [];
    let objectFieldNames = [];
    collectSchemaPropertyKeys(schema, "", fieldNames, objectFieldNames);
    return _.difference(_.uniq(fieldNames), _.uniq(objectFieldNames));
}

export function getSchemaFieldPath(schema, fieldName) {
    let schemaFieldNames = getSchemaPropertyKeys(schema);
    if (_.includes(schemaFieldNames, fieldName)) {
        return fieldName;
    }

    let matchingFieldNames = _.filter(
        schemaFieldNames,
        function (schemaFieldName) {
            return _.last(schemaFieldName.split("/")) === fieldName;
        },
    );
    return matchingFieldNames.length === 1 ? matchingFieldNames[0] : fieldName;
}

export function getSchemaFieldPathSegments(schema, fieldName) {
    let schemaFieldPath = getSchemaFieldPath(schema, fieldName);
    if (!_.includes(getSchemaPropertyKeys(schema), schemaFieldPath)) {
        return [];
    }

    return getSchemaPathSegments(schema, schemaFieldPath);
}

export function getSchemaProperty(schema, fieldName) {
    return getSchemaPropertyAtPath(
        schema,
        getSchemaFieldPath(schema, fieldName),
    );
}

function collectRequiredSchemaPropertyKeys(schema, prefix) {
    let schemaProperties = _.get(schema, "properties", {});
    let requiredPropertyNames = _.get(schema, "required", []);
    let requiredFieldNames = [];

    _.forEach(requiredPropertyNames, function (propertyName) {
        let propertySchema = schemaProperties[propertyName];
        if (!propertySchema) {
            return;
        }

        let fieldPath = prefix ? prefix + "/" + propertyName : propertyName;
        if (isSchemaObjectContainer(propertySchema)) {
            let nestedRequiredFieldNames = collectRequiredSchemaPropertyKeys(
                propertySchema,
                fieldPath,
            );
            requiredFieldNames = requiredFieldNames.concat(
                nestedRequiredFieldNames.length > 0
                    ? nestedRequiredFieldNames
                    : [fieldPath],
            );
            return;
        }

        requiredFieldNames.push(fieldPath);
    });

    return requiredFieldNames;
}

export function getRequiredSchemaPropertyKeys(schema) {
    return collectRequiredSchemaPropertyKeys(schema, "");
}

export function getNestedFormDataForSchema(formData, schema) {
    if (!_.isPlainObject(formData)) {
        return {};
    }

    if (!schema || schema.type !== "object") {
        return formData;
    }

    let schemaFormData = {};
    _.forEach(getSchemaPropertyKeys(schema), function (fieldName) {
        let fieldData = getDataForSchemaField(formData, schema, fieldName);
        if (fieldData.found) {
            setDataForSchemaField(
                schemaFormData,
                schema,
                fieldName,
                fieldData.value,
            );
        }
    });
    return schemaFormData;
}

export function getFlatFormDataForSchema(formData, schema) {
    if (!_.isPlainObject(formData)) {
        return {};
    }

    if (!schema || schema.type !== "object") {
        return formData;
    }

    let flatFormData = {};
    _.forEach(getSchemaPropertyKeys(schema), function (fieldName) {
        let fieldData = getDataForSchemaField(formData, schema, fieldName);
        if (fieldData.found) {
            flatFormData[fieldName] = fieldData.value;
        }
    });
    return flatFormData;
}

export function getSchemaDataValue(formData, schema, fieldName) {
    let schemaFieldPath = getSchemaFieldPath(schema, fieldName);
    let fieldData = getDataForSchemaField(formData, schema, schemaFieldPath);
    if (fieldData.found) {
        return fieldData.value;
    }

    return _.get(getDataForSchemaField(formData, schema, fieldName), "value");
}

export function setSchemaDataValue(formData, schema, fieldName, value) {
    setDataForSchemaField(
        formData,
        schema,
        getSchemaFieldPath(schema, fieldName),
        value,
    );
    return formData;
}

function dataValueMatchesSchemaType(value, schemaType) {
    if (schemaType === "array") {
        return _.isArray(value);
    }
    if (schemaType === "boolean") {
        return typeof value === "boolean";
    }
    if (schemaType === "integer") {
        return Number.isInteger(value);
    }
    if (schemaType === "null") {
        return value === null;
    }
    if (schemaType === "number") {
        return typeof value === "number" && Number.isFinite(value);
    }
    if (schemaType === "object") {
        return _.isPlainObject(value);
    }
    if (schemaType === "string") {
        return typeof value === "string";
    }
    return true;
}

function getDataValueSchemaTypeCompatibility(value, schemaProperty) {
    let propertyType = _.get(schemaProperty, "type");
    let hasTypeConstraint = propertyType !== undefined;
    let isCompatible = true;

    if (hasTypeConstraint) {
        let propertyTypes = _.isArray(propertyType)
            ? propertyType
            : [propertyType];
        isCompatible = _.some(propertyTypes, function (schemaType) {
            return dataValueMatchesSchemaType(value, schemaType);
        });
    }

    _.forEach(_.get(schemaProperty, "allOf", []), function (childSchema) {
        let childCompatibility = getDataValueSchemaTypeCompatibility(
            value,
            childSchema,
        );
        if (childCompatibility.hasTypeConstraint) {
            hasTypeConstraint = true;
            isCompatible = isCompatible && childCompatibility.isCompatible;
        }
    });

    _.forEach(["anyOf", "oneOf"], function (schemaKeyword) {
        let childCompatibilities = _.map(
            _.get(schemaProperty, schemaKeyword, []),
            function (childSchema) {
                return getDataValueSchemaTypeCompatibility(value, childSchema);
            },
        );
        if (
            childCompatibilities.length > 0 &&
            _.every(childCompatibilities, "hasTypeConstraint")
        ) {
            hasTypeConstraint = true;
            isCompatible =
                isCompatible && _.some(childCompatibilities, "isCompatible");
        }
    });

    let conditionalCompatibilities = _.map(
        _.filter([schemaProperty.then, schemaProperty.else]),
        function (conditionalSchema) {
            return getDataValueSchemaTypeCompatibility(
                value,
                conditionalSchema,
            );
        },
    );
    if (
        conditionalCompatibilities.length > 0 &&
        _.every(conditionalCompatibilities, "hasTypeConstraint")
    ) {
        hasTypeConstraint = true;
        isCompatible =
            isCompatible && _.some(conditionalCompatibilities, "isCompatible");
    }

    return { hasTypeConstraint, isCompatible };
}

function dataValueIsCompatibleWithSchemaProperties(value, schemaProperties) {
    let typeCompatibilities = _.filter(
        _.map(schemaProperties, function (schemaProperty) {
            return getDataValueSchemaTypeCompatibility(value, schemaProperty);
        }),
        "hasTypeConstraint",
    );

    return (
        typeCompatibilities.length === 0 ||
        _.some(typeCompatibilities, "isCompatible")
    );
}

function normalizeFormDataForSchema(formData, schema) {
    let normalizedFormData = _.cloneDeep(formData);
    _.forEach(getSchemaPropertyKeys(schema), function (fieldName) {
        if (hasOwn(normalizedFormData, fieldName)) {
            delete normalizedFormData[fieldName];
        }

        let fieldData = getDataForSchemaField(formData, schema, fieldName);
        if (fieldData.found) {
            if (
                !dataValueIsCompatibleWithSchemaProperties(
                    fieldData.value,
                    getSchemaPropertiesAtPath(schema, fieldName),
                )
            ) {
                _.unset(
                    normalizedFormData,
                    getSchemaPathSegments(schema, fieldName),
                );
                return;
            }
            setDataForSchemaField(
                normalizedFormData,
                schema,
                fieldName,
                fieldData.value,
            );
        }
    });
    return normalizedFormData;
}

function sanitizeSchemaDataNode(formData, schema) {
    if (!_.isPlainObject(formData) || !schema || schema.type !== "object") {
        return formData;
    }

    let schemaProperties = _.get(schema, "properties", {});
    let sanitizedData =
        schema.additionalProperties === false ? {} : _.assign({}, formData);

    _.forEach(schemaProperties, function (schemaProperty, propertyName) {
        if (!hasOwn(formData, propertyName)) {
            return;
        }

        let value = formData[propertyName];
        if (
            !dataValueIsCompatibleWithSchemaProperties(value, [schemaProperty])
        ) {
            return;
        }

        if (isSchemaObjectContainer(schemaProperty) && _.isPlainObject(value)) {
            sanitizedData[propertyName] = sanitizeSchemaDataNode(
                value,
                schemaProperty,
            );
            return;
        }

        sanitizedData[propertyName] = value;
    });

    if (schema.additionalProperties === false) {
        return _.pick(sanitizedData, _.keys(schemaProperties));
    }
    return sanitizedData;
}

export function sanitizeFormDataForSchema(formData, schema) {
    if (!_.isPlainObject(formData)) {
        return {};
    }

    if (!schema || schema.type !== "object") {
        return formData;
    }

    let normalizedFormData = normalizeFormDataForSchema(formData, schema);
    return sanitizeSchemaDataNode(normalizedFormData, schema);
}

export function getReactSchemaFormData(formData, schema) {
    let normalizedFormData = sanitizeFormDataForSchema(
        getNestedFormDataForSchema(formData, schema),
        schema,
    );
    _.forEach(getSchemaPropertyKeys(schema), function (fieldName) {
        let fieldData = getDataForSchemaField(
            normalizedFormData,
            schema,
            fieldName,
        );
        if (fieldData.found && fieldData.value === "") {
            setDataForSchemaField(
                normalizedFormData,
                schema,
                fieldName,
                undefined,
            );
        }
    });
    return normalizedFormData;
}

export function getDocumentFieldPathData(formData, schema) {
    return getFlatFormDataForSchema(
        sanitizeFormDataForSchema(formData, schema),
        schema,
    );
}

function getAllowedPropertyNamesAtPath(allowedFieldNames, currentPath) {
    let allowedPropertyNames = [];
    let seenPropertyNames = {};
    let currentPathPrefix = currentPath ? currentPath + "/" : "";

    _.forEach(allowedFieldNames, function (allowedFieldName) {
        if (!_.isString(allowedFieldName)) {
            return;
        }

        if (
            currentPathPrefix &&
            !allowedFieldName.startsWith(currentPathPrefix)
        ) {
            return;
        }

        let relativeFieldPath = currentPathPrefix
            ? allowedFieldName.slice(currentPathPrefix.length)
            : allowedFieldName;
        if (!relativeFieldPath) {
            return;
        }

        let separatorIndex = relativeFieldPath.indexOf("/");
        let propertyName =
            separatorIndex === -1
                ? relativeFieldPath
                : relativeFieldPath.slice(0, separatorIndex);
        if (!propertyName || hasOwn(seenPropertyNames, propertyName)) {
            return;
        }

        seenPropertyNames[propertyName] = true;
        allowedPropertyNames.push(propertyName);
    });

    return allowedPropertyNames;
}

function pruneSchemaForAllowedFields(
    schemaNode,
    allowedFieldNames,
    currentPath = "",
) {
    if (_.isArray(schemaNode)) {
        return _.map(schemaNode, function (childNode) {
            return pruneSchemaForAllowedFields(
                childNode,
                allowedFieldNames,
                currentPath,
            );
        });
    }

    if (!_.isPlainObject(schemaNode)) {
        return schemaNode;
    }

    let prunedNode = _.cloneDeep(schemaNode);
    let allowedPropertyNamesAtPath = getAllowedPropertyNamesAtPath(
        allowedFieldNames,
        currentPath,
    );

    if (_.isArray(prunedNode.allOf)) {
        prunedNode.allOf = _.map(prunedNode.allOf, function (allOfSchemaNode) {
            return pruneSchemaForAllowedFields(
                allOfSchemaNode,
                allowedFieldNames,
                currentPath,
            );
        });
    }

    if (_.isArray(prunedNode.anyOf)) {
        prunedNode.anyOf = _.map(prunedNode.anyOf, function (anyOfSchemaNode) {
            return pruneSchemaForAllowedFields(
                anyOfSchemaNode,
                allowedFieldNames,
                currentPath,
            );
        });
    }

    if (_.isArray(prunedNode.oneOf)) {
        prunedNode.oneOf = _.map(prunedNode.oneOf, function (oneOfSchemaNode) {
            return pruneSchemaForAllowedFields(
                oneOfSchemaNode,
                allowedFieldNames,
                currentPath,
            );
        });
    }

    if (hasOwn(prunedNode, "if")) {
        prunedNode.if = pruneSchemaForAllowedFields(
            prunedNode.if,
            allowedFieldNames,
            currentPath,
        );
    }

    if (hasOwn(prunedNode, "then")) {
        prunedNode.then = pruneSchemaForAllowedFields(
            prunedNode.then,
            allowedFieldNames,
            currentPath,
        );
    }

    if (hasOwn(prunedNode, "else")) {
        prunedNode.else = pruneSchemaForAllowedFields(
            prunedNode.else,
            allowedFieldNames,
            currentPath,
        );
    }

    let schemaProperties = _.get(schemaNode, "properties");
    if (_.isPlainObject(schemaProperties)) {
        let prunedProperties = {};
        _.forEach(schemaProperties, function (propertySchema, propertyName) {
            if (!_.includes(allowedPropertyNamesAtPath, propertyName)) {
                return;
            }

            let fieldPath = currentPath
                ? currentPath + "/" + propertyName
                : propertyName;
            if (_.includes(allowedFieldNames, fieldPath)) {
                prunedProperties[propertyName] = _.cloneDeep(propertySchema);
                return;
            }

            prunedProperties[propertyName] = pruneSchemaForAllowedFields(
                propertySchema,
                allowedFieldNames,
                fieldPath,
            );
        });
        prunedNode.properties = prunedProperties;
    }

    if (_.isArray(prunedNode.required)) {
        prunedNode.required = _.filter(
            prunedNode.required,
            function (propertyName) {
                return _.includes(allowedPropertyNamesAtPath, propertyName);
            },
        );
    }

    if (_.isPlainObject(prunedNode.dependencies)) {
        prunedNode.dependencies = _.pickBy(
            _.mapValues(prunedNode.dependencies, function (dependencyValue) {
                if (_.isArray(dependencyValue)) {
                    return _.filter(dependencyValue, function (fieldName) {
                        return _.includes(
                            allowedPropertyNamesAtPath,
                            fieldName,
                        );
                    });
                }

                return pruneSchemaForAllowedFields(
                    dependencyValue,
                    allowedFieldNames,
                    currentPath,
                );
            }),
            function (dependencyValue, dependencyFieldName) {
                if (
                    !_.includes(allowedPropertyNamesAtPath, dependencyFieldName)
                ) {
                    return false;
                }

                if (_.isArray(dependencyValue)) {
                    return dependencyValue.length > 0;
                }

                return true;
            },
        );
    }

    if (_.isPlainObject(prunedNode.dependentRequired)) {
        prunedNode.dependentRequired = _.pickBy(
            _.mapValues(
                prunedNode.dependentRequired,
                function (dependentRequiredFieldNames) {
                    return _.filter(
                        dependentRequiredFieldNames,
                        function (fieldName) {
                            return _.includes(
                                allowedPropertyNamesAtPath,
                                fieldName,
                            );
                        },
                    );
                },
            ),
            function (dependentRequiredFieldNames, dependencyFieldName) {
                return (
                    _.includes(
                        allowedPropertyNamesAtPath,
                        dependencyFieldName,
                    ) && dependentRequiredFieldNames.length > 0
                );
            },
        );
    }

    if (_.isPlainObject(prunedNode.dependentSchemas)) {
        prunedNode.dependentSchemas = _.pickBy(
            _.mapValues(
                prunedNode.dependentSchemas,
                function (dependentSchema) {
                    return pruneSchemaForAllowedFields(
                        dependentSchema,
                        allowedFieldNames,
                        currentPath,
                    );
                },
            ),
            function (dependentSchema, dependencyFieldName) {
                return _.includes(
                    allowedPropertyNamesAtPath,
                    dependencyFieldName,
                );
            },
        );
    }

    if (
        prunedNode.additionalProperties === false &&
        _.isPlainObject(prunedNode.properties) &&
        _.isEmpty(prunedNode.properties)
    ) {
        delete prunedNode.additionalProperties;
    }

    return prunedNode;
}

export function buildSchemaForAllowedFields(schema, allowedFieldNames) {
    return pruneSchemaForAllowedFields(schema, allowedFieldNames || []);
}

function collectSignatureFieldNames(
    schemaNode,
    currentPath,
    signatureFieldNames,
) {
    if (!_.isPlainObject(schemaNode)) {
        return;
    }

    if (schemaNode["x-input-type"] === "signature") {
        if (currentPath) {
            signatureFieldNames.push(currentPath);
        }
        return;
    }

    _.forEach(schemaNode.properties, function (propertySchema, propertyName) {
        let fieldPath = currentPath
            ? currentPath + "/" + propertyName
            : propertyName;
        collectSignatureFieldNames(
            propertySchema,
            fieldPath,
            signatureFieldNames,
        );
    });

    _.forEach(["allOf", "anyOf", "oneOf"], function (schemaKeyword) {
        _.forEach(schemaNode[schemaKeyword], function (childSchema) {
            collectSignatureFieldNames(
                childSchema,
                currentPath,
                signatureFieldNames,
            );
        });
    });

    _.forEach(["if", "then", "else"], function (schemaKeyword) {
        collectSignatureFieldNames(
            schemaNode[schemaKeyword],
            currentPath,
            signatureFieldNames,
        );
    });

    _.forEach(schemaNode.dependencies, function (dependencySchema) {
        if (!_.isArray(dependencySchema)) {
            collectSignatureFieldNames(
                dependencySchema,
                currentPath,
                signatureFieldNames,
            );
        }
    });

    _.forEach(schemaNode.dependentSchemas, function (dependentSchema) {
        collectSignatureFieldNames(
            dependentSchema,
            currentPath,
            signatureFieldNames,
        );
    });
}

export function getSignatureFieldNames(documentPackageTranslated) {
    let schema = _.get(documentPackageTranslated, "schema");
    let signatureFieldNames = [];
    collectSignatureFieldNames(schema, "", signatureFieldNames);
    return _.uniq(signatureFieldNames);
}

export function filterOutBadEnums(prefillData, formSchema) {
    let filteredData = getFlatFormDataForSchema(prefillData, formSchema);
    _.forEach(getSchemaPropertyKeys(formSchema), function (fieldName) {
        let schemaProperty = getSchemaPropertyAtPath(formSchema, fieldName);
        if (
            hasOwn(filteredData, fieldName) &&
            schemaProperty &&
            schemaProperty.enum &&
            !_.includes(schemaProperty.enum, filteredData[fieldName])
        ) {
            filteredData[fieldName] = "";
        }
    });
    return filteredData;
}

export function buildRoleScopedFormSchema(
    documentPackageTranslated,
    documentPackage,
    documentPackageRequest,
    role,
) {
    let signatureFieldNames = getSignatureFieldNames(documentPackageTranslated);
    let signerFields = _.get(documentPackage, ["fields", role], []);
    if (documentPackageRequest.document_package_customization) {
        signerFields = _.difference(
            signerFields,
            _.get(documentPackage, "subject_fields.requester", []),
        );
    }

    let schema = _.cloneDeep(documentPackageTranslated.schema);
    let fieldsThatPeopleCouldFillOut = _.flatten(
        _.values(_.get(documentPackage, "fields", {})),
    );
    let allowedFieldNames = _.difference(
        _.intersection(signerFields, fieldsThatPeopleCouldFillOut),
        signatureFieldNames,
    );
    return buildSchemaForAllowedFields(schema, allowedFieldNames);
}

function normalizeUiFieldPath(fieldName) {
    return _.isString(fieldName) ? fieldName.replace(/\./g, "/") : "";
}

function schemaHasFieldOrChildren(fieldNames, normalizedFieldPath) {
    if (!normalizedFieldPath) {
        return false;
    }

    if (_.includes(fieldNames, normalizedFieldPath)) {
        return true;
    }

    let fieldPathPrefix = normalizedFieldPath + "/";
    return _.some(fieldNames, function (fieldName) {
        return _.startsWith(fieldName, fieldPathPrefix);
    });
}

function trimLayoutGridChild(child, fieldNames) {
    if (_.isString(child)) {
        return schemaHasFieldOrChildren(fieldNames, normalizeUiFieldPath(child))
            ? child
            : null;
    }

    if (!_.isPlainObject(child)) {
        return null;
    }

    if (hasOwn(child, "ui:row")) {
        let trimmedRow = trimLayoutGridNode(child["ui:row"], fieldNames);
        return trimmedRow ? { "ui:row": trimmedRow } : null;
    }

    if (hasOwn(child, "ui:col")) {
        let trimmedCol = trimLayoutGridNode(child["ui:col"], fieldNames);
        return trimmedCol ? { "ui:col": trimmedCol } : null;
    }

    if (hasOwn(child, "ui:columns")) {
        let trimmedColumns = trimLayoutGridNode(
            child["ui:columns"],
            fieldNames,
        );
        return trimmedColumns ? { "ui:columns": trimmedColumns } : null;
    }

    if (hasOwn(child, "ui:condition")) {
        let trimmedCondition = trimLayoutGridCondition(
            child["ui:condition"],
            fieldNames,
        );
        return trimmedCondition ? { "ui:condition": trimmedCondition } : null;
    }

    if (hasOwn(child, "name")) {
        if (!child.name) {
            return _.cloneDeep(child);
        }

        return schemaHasFieldOrChildren(
            fieldNames,
            normalizeUiFieldPath(child.name),
        )
            ? _.cloneDeep(child)
            : null;
    }

    return _.cloneDeep(child);
}

function trimLayoutGridChildren(children, fieldNames) {
    let normalizedChildren = _.isArray(children) ? children : [];
    return _.compact(
        _.map(normalizedChildren, function (child) {
            return trimLayoutGridChild(child, fieldNames);
        }),
    );
}

function trimLayoutGridNode(node, fieldNames) {
    let normalizedNode = _.isArray(node)
        ? { children: node }
        : _.cloneDeep(node);
    if (!_.isPlainObject(normalizedNode)) {
        return null;
    }

    let trimmedChildren = trimLayoutGridChildren(
        normalizedNode.children,
        fieldNames,
    );
    if (trimmedChildren.length === 0) {
        return null;
    }

    normalizedNode.children = trimmedChildren;
    return normalizedNode;
}

function trimLayoutGridCondition(conditionNode, fieldNames) {
    if (!_.isPlainObject(conditionNode)) {
        return null;
    }

    if (
        !schemaHasFieldOrChildren(
            fieldNames,
            normalizeUiFieldPath(conditionNode.field),
        )
    ) {
        return null;
    }

    let trimmedConditionNode = _.cloneDeep(conditionNode);
    let trimmedChildren = trimLayoutGridChildren(
        conditionNode.children,
        fieldNames,
    );
    if (trimmedChildren.length === 0) {
        return null;
    }

    trimmedConditionNode.children = trimmedChildren;
    return trimmedConditionNode;
}

function trimUiSchemaRootOverride(overrideNode, schemaProperty) {
    if (!_.isPlainObject(overrideNode)) {
        return overrideNode;
    }

    let nestedSchemaProperties = _.get(schemaProperty, "properties", {});
    if (!_.isPlainObject(nestedSchemaProperties)) {
        return _.cloneDeep(overrideNode);
    }

    return _.pickBy(_.cloneDeep(overrideNode), function (value, key) {
        return _.startsWith(key, "ui:") || hasOwn(nestedSchemaProperties, key);
    });
}

export function trimUiSchemaForSchema(uiSchema, schema) {
    if (!_.isPlainObject(uiSchema)) {
        return uiSchema;
    }

    if (!_.isPlainObject(schema)) {
        return _.cloneDeep(uiSchema);
    }

    let trimmedUiSchema = _.cloneDeep(uiSchema);
    let fieldNames = getSchemaPropertyKeys(schema);
    let trimmedRow = trimLayoutGridNode(
        _.get(trimmedUiSchema, ["ui:layoutGrid", "ui:row"]),
        fieldNames,
    );

    if (trimmedRow) {
        _.set(trimmedUiSchema, ["ui:layoutGrid", "ui:row"], trimmedRow);
    } else {
        _.set(trimmedUiSchema, ["ui:layoutGrid", "ui:row"], { children: [] });
    }

    let schemaProperties = _.get(schema, "properties", {});
    _.forEach(_.keys(trimmedUiSchema), function (rootKey) {
        if (_.startsWith(rootKey, "ui:")) {
            return;
        }

        if (!hasOwn(schemaProperties, rootKey)) {
            delete trimmedUiSchema[rootKey];
            return;
        }

        trimmedUiSchema[rootKey] = trimUiSchemaRootOverride(
            trimmedUiSchema[rootKey],
            schemaProperties[rootKey],
        );
    });

    return trimmedUiSchema;
}
